/**
 * Площадки экологической структуры организации (ecology-sites, вариант Б): таблица «Площадка / Код
 * НВОС / Категория», добавление и правка в модалке, удаление с подтверждением. Основная площадка
 * первой, удалить её нельзя. Одинаково у клиента (обзор «Экология») и у команды (вкладка «Экология»
 * карточки клиента) — отличается только API скоупа; категорию, указанную командой, сервер помечает
 * как указанную экологом Центрики. Колонка «Опрос» (ecology-survey) — статус опроса по площадке и
 * переход в мастер опроса.
 */
import { useState } from 'react'
import { ClipboardList, MapPin, Pencil, Plus, Trash2 } from 'lucide-react'
import type { EcoOverview, EcoScopeApi, EcoSite } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ecoCategorySourceLabel, ecoSiteCategoryLabel, ecoSurveyStatusText } from '@/lib/ecology'
import { Panel } from '@/design-system/components/layout/panel'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { SiteDialog } from './site-dialog'

type DialogState = { open: false } | { open: true; site: EcoSite | null }

export function SitesPanel({
  api,
  overview,
  onChange,
  onSurvey,
  byTeam = false,
}: {
  api: EcoScopeApi
  overview: EcoOverview
  onChange: (overview: EcoOverview) => void
  /** Открыть опрос по площадке. */
  onSurvey: (site: EcoSite) => void
  /** Команда Центрики: подсказка у площадки без категории после опроса — проставить категорию. */
  byTeam?: boolean
}) {
  const [dialog, setDialog] = useState<DialogState>({ open: false })
  const [confirmDelete, setConfirmDelete] = useState<EcoSite | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function remove() {
    if (!confirmDelete) return
    setError(null)
    setDeleting(true)
    try {
      onChange(await api.deleteSite(confirmDelete.id))
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось удалить площадку'))
    } finally {
      setDeleting(false)
      setConfirmDelete(null)
    }
  }

  const itemsOf = (siteId: string) => overview.items.filter((i) => i.siteId === siteId).length

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-16 font-semibold text-txt">Площадки</h2>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => setDialog({ open: true, site: null })}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить площадку
        </Button>
      </div>
      {error ? (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      ) : null}
      <Panel>
        <div className="overflow-x-auto">
          <DataTable>
            <DataTableHead>
              <tr>
                <DataTableHeaderCell>Площадка</DataTableHeaderCell>
                <DataTableHeaderCell>Код объекта НВОС</DataTableHeaderCell>
                <DataTableHeaderCell>Категория</DataTableHeaderCell>
                <DataTableHeaderCell>Опрос</DataTableHeaderCell>
                <DataTableHeaderCell>{''}</DataTableHeaderCell>
              </tr>
            </DataTableHead>
            <DataTableBody>
              {overview.sites.map((site) => (
                <DataTableRow key={site.id}>
                  <DataTableCell>
                    <div className="flex items-start gap-2">
                      <MapPin
                        className="mt-0.5 size-4 shrink-0 stroke-current text-txt-tertiary"
                        aria-hidden
                      />
                      <div className="min-w-0">
                        <div className="font-medium text-txt">
                          {site.name}
                          {site.isMain ? (
                            <span className="ml-2 text-12 font-normal text-txt-tertiary">
                              основная
                            </span>
                          ) : null}
                        </div>
                        {site.address ? (
                          <div className="text-12 text-txt-secondary">{site.address}</div>
                        ) : null}
                      </div>
                    </div>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap text-txt-secondary">
                    {site.nvosCode || '—'}
                  </DataTableCell>
                  <DataTableCell>
                    <Badge variant={site.category === '' ? 'amber' : 'gray'}>
                      {ecoSiteCategoryLabel(site.category)}
                    </Badge>
                    <div className="mt-1 text-12 text-txt-tertiary">
                      {site.category !== ''
                        ? ecoCategorySourceLabel(site)
                        : site.survey.status !== 'completed'
                          ? 'чтобы узнать категорию, пройдите опрос по площадке'
                          : byTeam
                            ? 'опрос пройден — проставьте категорию по ответам'
                            : 'по ответам опроса категорию проставит эколог Центрики'}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="whitespace-nowrap">
                    <div
                      className={
                        site.survey.status === 'completed' ? 'text-txt-secondary' : 'text-amber'
                      }
                    >
                      {ecoSurveyStatusText(site)}
                    </div>
                  </DataTableCell>
                  <DataTableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      <Button
                        size="sm"
                        variant={site.survey.status === 'completed' ? 'secondary' : 'primary'}
                        className="whitespace-nowrap"
                        onClick={() => onSurvey(site)}
                        leftIcon={<ClipboardList className="size-4 stroke-current" aria-hidden />}
                        aria-label={`${site.survey.status === 'completed' ? 'Опрос' : 'Пройти опрос'} по площадке «${site.name}»`}
                      >
                        {site.survey.status === 'completed' ? 'Опрос' : 'Пройти опрос'}
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        title="Изменить"
                        aria-label={`Изменить площадку «${site.name}»`}
                        onClick={() => setDialog({ open: true, site })}
                      >
                        <Pencil className="size-4 stroke-current" aria-hidden />
                      </Button>
                      {site.isMain ? null : (
                        <Button
                          size="sm"
                          variant="secondary"
                          title="Удалить"
                          aria-label={`Удалить площадку «${site.name}»`}
                          onClick={() => setConfirmDelete(site)}
                        >
                          <Trash2 className="size-4 stroke-current" aria-hidden />
                        </Button>
                      )}
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </DataTableBody>
          </DataTable>
        </div>
      </Panel>

      <SiteDialog
        open={dialog.open}
        site={dialog.open ? dialog.site : null}
        api={api}
        onClose={() => setDialog({ open: false })}
        onSaved={(next) => {
          onChange(next)
          setDialog({ open: false })
        }}
      />

      <ConfirmDialog
        open={confirmDelete !== null}
        title="Удаление площадки"
        message={
          confirmDelete
            ? itemsOf(confirmDelete.id) > 0
              ? `Удалить площадку «${confirmDelete.name}»? Вместе с ней удалятся её документы и отчётность в разделе (${itemsOf(confirmDelete.id)}).`
              : `Удалить площадку «${confirmDelete.name}»?`
            : ''
        }
        onClose={() => setConfirmDelete(null)}
        closeDisabled={deleting}
        buttons={[
          {
            label: deleting ? 'Удаление…' : 'Удалить',
            variant: 'danger',
            disabled: deleting,
            onClick: () => void remove(),
          },
          {
            label: 'Отмена',
            variant: 'secondary',
            disabled: deleting,
            onClick: () => setConfirmDelete(null),
          },
        ]}
      />
    </div>
  )
}
